// src/data/dilemmaQuestions.js
// 20 gut-punch moral dilemma scenarios with 4 options each.
// Q1–Q10: original quiz (rebalanced for dimension parity).
// Q11–Q20: new scenarios designed to (a) cover the under-sampled value
// dimensions (AUTONOMY, SANCTITY, PROGRESS, TRADITION) so cluster
// distribution evens out, and (b) include the "lie under oath" question
// promised on the landing page.

export const dilemmaQuestions = [
  {
    id: 'q1',
    type: 'scenario',
    text: "Your best friend just lost their job and asks to borrow $2,000. You have it, but it's your emergency fund.",
    options: [
      {
        key: 'a',
        text: 'How quickly can I get them the money? This is what friends are for.',
      },
      {
        key: 'b',
        text: 'I need to protect my own security first. What if I need it?',
      },
      {
        key: 'c',
        text: "Let me help them figure out a plan that doesn't risk my stability.",
      },
      {
        key: 'd',
        text: "Why aren't there better systems to prevent this situation?",
      },
    ],
  },
  {
    id: 'q2',
    type: 'scenario',
    text: 'You overhear coworkers making jokes that could be considered racist. You:',
    options: [
      {
        key: 'a',
        text: 'Report to HR immediately. This is unacceptable and must be documented.',
      },
      {
        key: 'b',
        text: "Pull them aside privately to explain why it's harmful.",
      },
      {
        key: 'c',
        text: "Stay silent. Not my battle, and I don't want the drama.",
      },
      {
        key: 'd',
        text: 'Speak up in the moment, even if it makes things awkward.',
      },
    ],
  },
  {
    id: 'q3',
    type: 'scenario',
    text: 'Your partner wants to move to a new city for a career opportunity. You have deep roots where you are.',
    options: [
      {
        key: 'a',
        text: "My family and community are here. That's non-negotiable.",
      },
      {
        key: 'b',
        text: 'Their career matters as much as mine. We figure it out together.',
      },
      {
        key: 'c',
        text: 'Honestly assess which choice maximizes our combined opportunities.',
      },
      {
        key: 'd',
        text: 'Support their dream even if it means sacrificing my comfort.',
      },
    ],
  },
  {
    id: 'q4',
    type: 'scenario',
    text: 'You discover your company is using a legal but ethically questionable practice to boost profits.',
    options: [
      {
        key: 'a',
        text: "Keep my head down. I need this job and it's not illegal.",
      },
      {
        key: 'b',
        text: 'Document everything and consider whistleblowing publicly.',
      },
      {
        key: 'c',
        text: "Start looking for a new job immediately. I can't be part of this.",
      },
      {
        key: 'd',
        text: 'Assess the actual harm being done before deciding.',
      },
    ],
  },
  {
    id: 'q5',
    type: 'scenario',
    text: 'A family member posts political views on social media that you strongly disagree with.',
    options: [
      {
        key: 'a',
        text: "Family is family. Political differences shouldn't matter.",
      },
      {
        key: 'b',
        text: 'Some views cross a line. I distance myself from them.',
      },
      {
        key: 'c',
        text: 'Respect their right to their views even if I disagree.',
      },
      {
        key: 'd',
        text: 'Post counter-perspectives on my own feed without directly engaging.',
      },
    ],
  },
  {
    id: 'q6',
    type: 'scenario',
    text: "You're offered a promotion with 40% more pay but 60-hour work weeks. You currently work 45 hours.",
    options: [
      {
        key: 'a',
        text: 'Take it. Success requires sacrifice and this is career advancement.',
      },
      {
        key: 'b',
        text: 'Decline. My work-life balance and wellbeing matter more than money.',
      },
      {
        key: 'c',
        text: 'Negotiate for the pay increase with reasonable hours.',
      },
      {
        key: 'd',
        text: 'Consider what my family and community expect of me.',
      },
    ],
  },
  {
    id: 'q7',
    type: 'scenario',
    text: "A less qualified coworker gets promoted over you because they're better at office politics.",
    options: [
      {
        key: 'a',
        text: 'Document the unfairness and escalate to HR or higher management.',
      },
      {
        key: 'b',
        text: 'Focus on my own work. Merit will eventually win out.',
      },
      {
        key: 'c',
        text: 'Start looking for a company that values actual competence.',
      },
      {
        key: 'd',
        text: "Respect the decision — leadership sees something I don't.",
      },
    ],
  },
  {
    id: 'q8',
    type: 'scenario',
    text: 'You have to fire someone who has been underperforming but has a family to support.',
    options: [
      {
        key: 'a',
        text: 'Do it quickly and professionally. Dragging it out helps no one.',
      },
      {
        key: 'b',
        text: 'Give them one more chance with a clear improvement plan.',
      },
      {
        key: 'c',
        text: "Follow company policy exactly. Personal circumstances can't influence business.",
      },
      {
        key: 'd',
        text: 'Question whether our metrics actually measure what matters.',
      },
    ],
  },
  {
    id: 'q9',
    type: 'scenario',
    text: 'Your teenage child wants to skip college to pursue a creative career.',
    options: [
      {
        key: 'a',
        text: 'Support their passion. Following your calling matters most.',
      },
      {
        key: 'b',
        text: 'Require college first. They need a backup plan and life experience.',
      },
      {
        key: 'c',
        text: 'Help them create a realistic business plan before deciding.',
      },
      {
        key: 'd',
        text: 'My job is to guide, not dictate their life path.',
      },
    ],
  },
  {
    id: 'q10',
    type: 'scenario',
    text: "Your child is being bullied at school. The school's response has been ineffective.",
    options: [
      {
        key: 'a',
        text: 'Escalate aggressively with school administration and district.',
      },
      {
        key: 'b',
        text: "Move them to a different school where they'll be safe.",
      },
      {
        key: 'c',
        text: 'Document everything and prepare legal action if necessary.',
      },
      {
        key: 'd',
        text: "Work with the bully's parents to address root causes.",
      },
    ],
  },

  // ------------------------------------------------------------------
  // New questions (Q11–Q20)
  // ------------------------------------------------------------------

  {
    id: 'q11',
    type: 'scenario',
    text: 'Your best friend asks you to lie under oath for them. They swear they’re innocent.',
    options: [
      {
        key: 'a',
        text: 'I won’t perjure myself, even for them. There are lines I don’t cross.',
      },
      {
        key: 'b',
        text: 'For them? Yes. Loyalty isn’t something you turn on and off.',
      },
      {
        key: 'c',
        text: 'It depends what’s actually at stake — for them and for me.',
      },
      {
        key: 'd',
        text: 'I’d help them build a real defense, not commit a crime.',
      },
    ],
  },
  {
    id: 'q12',
    type: 'scenario',
    text: 'You catch your boss falsifying expense reports. There’s an anonymous tip line.',
    options: [
      {
        key: 'a',
        text: 'Drop the tip. Anonymous channels exist exactly for this.',
      },
      {
        key: 'b',
        text: 'Confront them directly first. People deserve a chance to explain.',
      },
      {
        key: 'c',
        text: 'Not my circus. They’ll get caught eventually without me.',
      },
      {
        key: 'd',
        text: 'Quietly document everything in case it matters later.',
      },
    ],
  },
  {
    id: 'q13',
    type: 'scenario',
    text: 'Your estranged parent dies and leaves you everything. Your sibling, who cared for them, gets nothing.',
    options: [
      {
        key: 'a',
        text: 'Split it equally. The will doesn’t reflect what’s actually fair.',
      },
      {
        key: 'b',
        text: 'Keep what they wanted me to have. It was their choice to make.',
      },
      {
        key: 'c',
        text: 'Let the courts sort it. I won’t play judge over family.',
      },
      {
        key: 'd',
        text: 'Donate it all. I don’t want money tangled up with that pain.',
      },
    ],
  },
  {
    id: 'q14',
    type: 'scenario',
    text: 'You used AI to write a cover letter that landed your dream job. They want you to start tomorrow.',
    options: [
      {
        key: 'a',
        text: 'Tell them. They should know what they’re actually hiring.',
      },
      {
        key: 'b',
        text: 'Take it. Everyone uses AI now — they’d do the same.',
      },
      {
        key: 'c',
        text: 'Take it and outwork the doubt. Earn it retroactively.',
      },
      {
        key: 'd',
        text: 'Use AI to do the actual job too. Modern problem, modern solution.',
      },
    ],
  },
  {
    id: 'q15',
    type: 'scenario',
    text: 'You’re dating someone amazing. You realize they have a small lie on their dating profile — height, age, nothing huge.',
    options: [
      {
        key: 'a',
        text: 'Bring it up. Trust starts with the small stuff.',
      },
      {
        key: 'b',
        text: 'Let it go. Everyone fudges their profile a little.',
      },
      {
        key: 'c',
        text: 'It tells me what else they might lie about. I’m out.',
      },
      {
        key: 'd',
        text: 'We connect in person — that’s what actually matters.',
      },
    ],
  },
  {
    id: 'q16',
    type: 'scenario',
    text: 'Your friend group is dunking on someone who isn’t there. The jokes are getting mean.',
    options: [
      {
        key: 'a',
        text: 'Speak up. You’d want them to do the same for you.',
      },
      {
        key: 'b',
        text: 'Stay quiet. It’s just jokes — I’ll catch heat for nothing.',
      },
      {
        key: 'c',
        text: 'Change the subject. No drama, no broken friendships.',
      },
      {
        key: 'd',
        text: 'Tell the absent person later. They deserve to know.',
      },
    ],
  },
  {
    id: 'q17',
    type: 'scenario',
    text: 'A close friend wants you to invest $5,000 in their new business. The plan is, frankly, terrible.',
    options: [
      {
        key: 'a',
        text: 'Give them the money. Friendship outranks financial logic.',
      },
      {
        key: 'b',
        text: 'Tell them honestly that the plan won’t work. They need truth, not capital.',
      },
      {
        key: 'c',
        text: 'Decline politely. Money plus friendship rarely ends well.',
      },
      {
        key: 'd',
        text: 'Help them refine the plan first. Invest only if it gets to viable.',
      },
    ],
  },
  {
    id: 'q18',
    type: 'scenario',
    text: 'A charity collector knocks at dinnertime with a story that feels exaggerated for sympathy.',
    options: [
      {
        key: 'a',
        text: 'Donate. The cause is real even if the story is shaped.',
      },
      {
        key: 'b',
        text: 'Decline politely. I prefer to vet causes properly before giving.',
      },
      {
        key: 'c',
        text: 'Skip it. I never give to door-to-door — it’s manipulation.',
      },
      {
        key: 'd',
        text: 'Take their info. Research first, then give if it checks out.',
      },
    ],
  },
  {
    id: 'q19',
    type: 'scenario',
    text: 'You watch someone shoplift baby formula at a grocery store. They look desperate.',
    options: [
      {
        key: 'a',
        text: 'Pretend I didn’t see. Some things aren’t mine to police.',
      },
      {
        key: 'b',
        text: 'Tell the manager. Rules exist for a reason.',
      },
      {
        key: 'c',
        text: 'Pay for it myself when I check out. Quietly.',
      },
      {
        key: 'd',
        text: 'Letting it slide isn’t compassion — it’s avoidance.',
      },
    ],
  },
  {
    id: 'q20',
    type: 'scenario',
    text: 'An old college roommate asks to crash on your couch indefinitely. They’re going through a rough patch.',
    options: [
      {
        key: 'a',
        text: 'Yes, of course. They’d do it for me.',
      },
      {
        key: 'b',
        text: 'Two weeks max — and a plan. Friendship has structure.',
      },
      {
        key: 'c',
        text: 'Help them find another place. I’ll cover the deposit if I have to.',
      },
      {
        key: 'd',
        text: 'Sorry. My space is the routine I’ve built. I need it.',
      },
    ],
  },
];

export default dilemmaQuestions;
